import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useCaptions } from '../context/CaptionContext';
import InlineWordEditor from './InlineWordEditor';
import { useI18n, rich } from '../../i18n';
import './TextProseRenderer.css';

// Icons for the floating bar
const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const TextProseRenderer = ({ searchMatchIds = new Set(), currentSearchMatchId = null, onSwitchToStudio, projectLanguage = 'ar', isTrialProtected = false, onVerifyClick }) => {
  const { t } = useI18n();
  const { 
    captionData,
    viewCaptionData, // Use this for display when respecting translation view
    isTranslationView,
    currentTime, 
    themeSettings, 
    getDisplayText, 
    displayMode,
    getPhraseForWord,
    autoScrollEnabled,
    seekToTime,
    videoRef,
    updateWord,
    updateViewWord, // Use this for edits to respect translation view
    addWord,
    removeWord,
    selectPhraseAlternative,
    updateCaptionData,
    changedWordIds,
    markWordsAsChanged,
    newWordIds,
    markWordsAsNew,
    showToast,
    mergeMultipleWords,
    getViewWordById // Use this to get word by ID respecting translation view
  } = useCaptions();
  
  // Use viewCaptionData which respects isTranslationView
  const displayData = viewCaptionData || captionData;
  
  const containerRef = useRef(null);
  const activeWordRef = useRef(null);
  const wordRefsMap = useRef({}); // Store refs to all word elements
  const [activeLineY, setActiveLineY] = useState(null);
  const [focusUpdateKey, setFocusUpdateKey] = useState(0); // Force re-render for focus
  const isArabizi = displayMode === 'arabizi';
  const isNonArabic = isTranslationView || ['en', 'fr', 'english', 'french', 'arabizi'].includes(projectLanguage);
  const shouldUppercaseText = isNonArabic && themeSettings.uppercaseEverything;
  const formatDisplayText = useCallback((value) => {
    if (!value || typeof value !== 'string') return value;
    return shouldUppercaseText ? value.toLocaleUpperCase() : value;
  }, [shouldUppercaseText]);
  const lastActiveWordRef = useRef(null);
  const lastActiveTimeRef = useRef(0);

  // Speaker features gated by env var
  const speakerFeaturesEnabled = process.env.REACT_APP_ENABLE_SPEAKER_FEATURES === 'true';
  const SPEAKER_COLORS = useMemo(() => ['#1e293b', '#9f1239'], []);

  // Build speaker-change metadata across all words in skeleton order
  const speakerMeta = useMemo(() => {
    if (!speakerFeaturesEnabled) return { speakerChangeIds: new Set(), wordSpeakerColorMap: {} };
    const speakerChangeIds = new Set();
    const wordSpeakerColorMap = {};
    const speakerIndexMap = {};
    let speakerCounter = 0;
    let prevSpeaker = null;

    const wordsById = {};
    for (const w of displayData.words) { wordsById[w.id] = w; }

    for (const skel of displayData.skeleton) {
      for (const wid of skel.words) {
        const word = wordsById[wid];
        if (!word || word.type === 'silence' || word.type === 'punctuation') continue;
        const speaker = word.speaker || null;
        if (speaker !== null) {
          if (!(speaker in speakerIndexMap)) {
            speakerIndexMap[speaker] = speakerCounter++;
          }
          wordSpeakerColorMap[wid] = SPEAKER_COLORS[speakerIndexMap[speaker] % SPEAKER_COLORS.length];
          if (prevSpeaker !== null && speaker !== prevSpeaker) {
            speakerChangeIds.add(wid);
          }
          prevSpeaker = speaker;
        }
      }
    }
    return { speakerChangeIds, wordSpeakerColorMap };
  }, [displayData.skeleton, displayData.words, SPEAKER_COLORS, speakerFeaturesEnabled]);
  
  const [selectedWord, setSelectedWord] = useState(null);
  const [popupAnchor, setPopupAnchor] = useState(null);
  const [lockedWordId, setLockedWordId] = useState(null);
  const [focusModeEnabled, setFocusModeEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const clickTimeoutRef = useRef(null);
  
  // Multi-word timing selection state
  const [timingSelectionWordIds, setTimingSelectionWordIds] = useState([]);
  const [timingSelectionStartWordId, setTimingSelectionStartWordId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMergeWordsModal, setShowMergeWordsModal] = useState(false);
  const [timingAdjustMode, setTimingAdjustMode] = useState('reassign'); // 'reassign' | 'proportional'
  const [audioSegments, setAudioSegments] = useState([]);
  const [wordAssignments, setWordAssignments] = useState({});
  const [selectedWordForPopup, setSelectedWordForPopup] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [playingSegmentId, setPlayingSegmentId] = useState(null);
  const [draggedWordId, setDraggedWordId] = useState(null);
  const [dragOverSegmentId, setDragOverSegmentId] = useState(null);
  const dragImageRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const timingSegmentEndRef = useRef(null);
  const timingSegmentListenerRef = useRef(null);
  
  // Context menu state for right-click
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuAnchorRef = useRef(null);

  // Use displayData to respect translation view
  const wordsMap = useMemo(() => {
    const map = {};
    for (const w of displayData.words) { map[w.id] = w; }
    return map;
  }, [displayData.words]);

  const captionWordsMap = useMemo(() => {
    if (captionData.words === displayData.words) return wordsMap;
    const map = {};
    for (const w of captionData.words) { map[w.id] = w; }
    return map;
  }, [captionData.words, displayData.words, wordsMap]);

  const activeSkeletons = useMemo(() => {
    return displayData.skeleton.filter(skel => !skel.disabled);
  }, [displayData.skeleton]);

  // Get ALL words in order from all skeletons
  const allWords = useMemo(() => {
    const words = [];
    activeSkeletons.forEach(skeleton => {
      skeleton.words.forEach(id => {
        const word = wordsMap[id];
        if (word && word.type !== 'silence') {
          words.push({ word, skeletonId: skeleton.id });
        }
      });
    });
    return words;
  }, [activeSkeletons, wordsMap]);

  // Calculate statistics
  const stats = useMemo(() => {
    // Count actual words (not punctuation or silence)
    const wordCount = allWords.filter(({ word }) => 
      word.type !== 'punctuation' && word.type !== 'silence'
    ).length;
    
    // Calculate total duration from first to last word
    const wordsWithTimes = displayData.words.filter(w => 
      w.type !== 'silence' && w.start_time !== undefined && w.end_time !== undefined
    );
    
    if (wordsWithTimes.length === 0) {
      return { wordCount, wpm: 0, duration: 0 };
    }
    
    const firstWord = wordsWithTimes[0];
    const lastWord = wordsWithTimes[wordsWithTimes.length - 1];
    const durationMinutes = (lastWord.end_time - firstWord.start_time) / 60;
    
    const wpm = durationMinutes > 0 ? Math.round(wordCount / durationMinutes) : 0;
    
    return { wordCount, wpm, duration: lastWord.end_time - firstWord.start_time };
  }, [allWords, displayData.words]);

  const clearTimingSegmentListener = useCallback(() => {
    const video = videoRef?.current;
    if (video && timingSegmentListenerRef.current) {
      video.removeEventListener('timeupdate', timingSegmentListenerRef.current);
    }
    timingSegmentListenerRef.current = null;
    timingSegmentEndRef.current = null;
  }, [videoRef]);

  // Reset segment playback on manual pause
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const handlePause = () => {
      clearTimingSegmentListener();
      setPlayingSegmentId(null);
      setIsPlaying(false);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    video.addEventListener('pause', handlePause);
    video.addEventListener('play', handlePlay);
    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('ended', handleEnded);
    };
  }, [videoRef, clearTimingSegmentListener]);

  const activeWordId = useMemo(() => {
    if (lockedWordId) return lockedWordId;
    
    const MINIMUM_HIGHLIGHT_DURATION = 0.2;
    
    let candidateWord = null;
    for (const word of displayData.words) {
      if (word.type === 'silence') continue;
      if (currentTime >= word.start_time && currentTime < word.end_time) {
        candidateWord = word;
        break;
      }
    }

    if (candidateWord && candidateWord.id !== lastActiveWordRef.current) {
      lastActiveWordRef.current = candidateWord.id;
      lastActiveTimeRef.current = currentTime;
      return candidateWord.id;
    }

    if (candidateWord && candidateWord.id === lastActiveWordRef.current) {
      return candidateWord.id;
    }

    if (lastActiveWordRef.current && (currentTime - lastActiveTimeRef.current) < MINIMUM_HIGHLIGHT_DURATION) {
      return lastActiveWordRef.current;
    }

    if (lastActiveWordRef.current) {
      return lastActiveWordRef.current;
    }

    lastActiveWordRef.current = null;
    return null;
  }, [displayData.words, currentTime, lockedWordId, isPlaying]);

  // Update the active line Y position when active word changes
  useEffect(() => {
    if (activeWordId && wordRefsMap.current[activeWordId]) {
      const activeElement = wordRefsMap.current[activeWordId];
      const rect = activeElement.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (containerRect) {
        // Store relative Y position within container
        setActiveLineY(rect.top - containerRect.top + containerRef.current.scrollTop);
        // Force re-render to update focus classes
        setFocusUpdateKey(k => k + 1);
      }
    } else if (!isPlaying) {
      // Only unfocus when audio is not playing
      setActiveLineY(null);
    }
  }, [activeWordId, isPlaying]);

  // Get focus class based on vertical distance from active word (progressive focus)
  const getFocusClass = (wordId) => {
    if (!focusModeEnabled || !isPlaying) return '';
    
    // If no active line yet (audio not playing), show everything at full opacity
    if (activeLineY === null) return '';
    
    const wordElement = wordRefsMap.current[wordId];
    if (!wordElement || !containerRef.current) return '';
    
    const wordRect = wordElement.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const wordY = wordRect.top - containerRect.top + containerRef.current.scrollTop;
    
    // Calculate vertical distance in pixels
    const verticalDistance = Math.abs(wordY - activeLineY);
    
    // Progressive focus thresholds (approximate line heights)
    const lineHeight = 50; // Approximate pixels per line
    
    // Progressive opacity levels based on distance
    if (verticalDistance < 20) return 'focus-active';      // Same line - 100% opacity
    if (verticalDistance < lineHeight * 1.5) return 'focus-near';  // 1 line away - 70% opacity
    if (verticalDistance < lineHeight * 2.5) return 'focus-mid';   // 2 lines away - 40% opacity
    return 'focus-far';  // 3+ lines away - 15% opacity
  };

  useEffect(() => {
    if (autoScrollEnabled && activeWordRef.current && containerRef.current) {
      activeWordRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeWordId, autoScrollEnabled]);

  // Scroll to current search match when it changes
  useEffect(() => {
    if (currentSearchMatchId && wordRefsMap.current[currentSearchMatchId]) {
      wordRefsMap.current[currentSearchMatchId].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [currentSearchMatchId]);

  // Keep word popup anchored to the word while scrolling/resizing (less sensitive)
  useEffect(() => {
    if (!selectedWord) return;
    let timeoutId = null;

    const updateRect = () => {
      const wordEl = wordRefsMap.current[selectedWord.id];
      if (!wordEl) return;
      const rect = wordEl.getBoundingClientRect();
      setPopupAnchor(rect);
    };

    const scheduleUpdate = () => {
      if (timeoutId) return;
      timeoutId = setTimeout(() => {
        timeoutId = null;
        updateRect();
      }, 80);
    };

    updateRect();
    const scrollEl = document.getElementById('editor-scroll');
    if (scrollEl) {
      scrollEl.addEventListener('scroll', scheduleUpdate, { passive: true });
    }
    window.addEventListener('scroll', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (scrollEl) {
        scrollEl.removeEventListener('scroll', scheduleUpdate);
      }
      window.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [selectedWord]);

  useEffect(() => {
    window.lockWordHighlight = (wordId) => setLockedWordId(wordId);
    window.unlockWordHighlight = () => setLockedWordId(null);
    
    // Clear timing selection and context menu when clicking outside
    const handleClickOutside = (e) => {
      if (!e.target.closest('.prose-word') && !e.target.closest('.timing-selection-bar') && !e.target.closest('.timing-confirm-overlay')) {
        setTimingSelectionWordIds([]);
        setTimingSelectionStartWordId(null);
      }
      // Close context menu when clicking outside
      if (!e.target.closest('.word-context-menu')) {
        setContextMenu(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      delete window.lockWordHighlight;
      delete window.unlockWordHighlight;
      document.removeEventListener('mousedown', handleClickOutside);
      // Clean up click timeout
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  // Track scroll for context menu position
  useEffect(() => {
    if (!contextMenu || !contextMenuAnchorRef.current) return;

    let lastUpdate = 0;
    const throttleMs = 80;

    const updateContextMenuPosition = () => {
      const now = Date.now();
      if (now - lastUpdate < throttleMs) return;
      lastUpdate = now;

      const el = contextMenuAnchorRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const topBarOffset = 72;
      setContextMenu(prev => prev ? {
        ...prev,
        x: rect.left + rect.width / 2,
        y: Math.max(rect.bottom + 4, topBarOffset)
      } : null);
    };

    const scrollContainer = document.getElementById('editor-scroll');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', updateContextMenuPosition);
    }
    window.addEventListener('scroll', updateContextMenuPosition);
    window.addEventListener('resize', updateContextMenuPosition);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', updateContextMenuPosition);
      }
      window.removeEventListener('scroll', updateContextMenuPosition);
      window.removeEventListener('resize', updateContextMenuPosition);
    };
  }, [contextMenu]);

  // Get a flat list of all word IDs in order across all skeletons
  const allWordIdsInOrder = useMemo(() => {
    const ids = [];
    activeSkeletons.forEach(skeleton => {
      skeleton.words.forEach(wid => {
        const word = wordsMap[wid];
        if (word && word.type !== 'silence') {
          ids.push(wid);
        }
      });
    });
    return ids;
  }, [activeSkeletons, wordsMap]);

  // Get all word IDs between two word IDs (inclusive), across all skeletons
  const getWordIdsBetween = (startId, endId) => {
    const startIndex = allWordIdsInOrder.indexOf(startId);
    const endIndex = allWordIdsInOrder.indexOf(endId);
    
    if (startIndex === -1 || endIndex === -1) return [];
    
    const minIdx = Math.min(startIndex, endIndex);
    const maxIdx = Math.max(startIndex, endIndex);
    
    return allWordIdsInOrder.slice(minIdx, maxIdx + 1);
  };

  // Check if we're in timing selection mode (has a start word selected)
  const isInTimingSelectionMode = timingSelectionStartWordId !== null;

  const handleWordClick = (e, word) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    
    // Clear any existing timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    // SHIFT+CLICK: Timing selection mode
    if (e.shiftKey) {
      // If we already have a start word, extend/update the range
      if (isInTimingSelectionMode) {
        const selectedIds = getWordIdsBetween(timingSelectionStartWordId, word.id);
        if (selectedIds.length > 0) {
          setTimingSelectionWordIds(selectedIds);
          // Close any popup
          setSelectedWord(null);
          setPopupAnchor(null);
          return;
        }
      }
      
      // First shift+click: start timing selection - reset confirm modal
      setShowConfirmModal(false);
      setTimingSelectionStartWordId(word.id);
      setTimingSelectionWordIds([word.id]);
      // Close any popup when starting selection
      setSelectedWord(null);
      setPopupAnchor(null);
      return;
    }
    
    // NORMAL CLICK: Show popup, clear any timing selection
    setShowConfirmModal(false);
    setTimingSelectionWordIds([]);
    setTimingSelectionStartWordId(null);
    
    // Delay single click action to allow double click detection
    clickTimeoutRef.current = setTimeout(() => {
      setPopupAnchor(rect);
      setSelectedWord(word);
    }, 200);
  };

  const handleSaveWord = (newText) => {
    if (selectedWord && newText !== selectedWord.text) {
      updateWord(selectedWord.id, { text: newText });
    }
    setSelectedWord(null);
    setPopupAnchor(null);
  };

  const handleWordDoubleClick = (e, word) => {
    e.stopPropagation();
    
    // Cancel the single click popup
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    
    // Clear any selected word / popup
    setSelectedWord(null);
    setPopupAnchor(null);
    
    // Clear timing selection too
    setTimingSelectionWordIds([]);
    setTimingSelectionStartWordId(null);
    
    // Seek to the word
    if (word.start_time !== undefined) {
      seekToTime(word.start_time);
    }
  };

  // Helper function to find skeleton index for a word
  const getSkeletonIndexForWord = (wordId) => {
    return displayData.skeleton.findIndex(skel => skel.words.includes(wordId));
  };

  // Right-click context menu handler
  const handleWordContextMenu = (e, word) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Close any open popup
    setSelectedWord(null);
    setPopupAnchor(null);
    
    // Store ref to the word element for scroll tracking
    contextMenuAnchorRef.current = e.target;
    
    // Find skeleton index for this word
    const skeletonIndex = getSkeletonIndexForWord(word.id);
    
    // Open context menu below the word (but not above top bar)
    const rect = e.target.getBoundingClientRect();
    const topBarOffset = 72;
    setContextMenu({
      x: rect.left + rect.width / 2,
      y: Math.max(rect.bottom + 4, topBarOffset),
      wordId: word.id,
      skeletonIndex
    });
  };

  const showPunctuation = themeSettings.showPunctuation !== false;

  const updateGhostPosition = useCallback((clientX, clientY) => {
    if (!dragImageRef.current || clientX === 0 || clientY === 0) return;
    dragImageRef.current.style.left = `${clientX - dragOffsetRef.current.x}px`;
    dragImageRef.current.style.top = `${clientY - dragOffsetRef.current.y}px`;
  }, []);

  const cleanupDragGhost = useCallback(() => {
    if (dragImageRef.current) {
      dragImageRef.current.remove();
      dragImageRef.current = null;
    }
  }, []);

  // Clean up drag image on unmount
  useEffect(() => {
    return () => {
      if (dragImageRef.current) {
        document.body.removeChild(dragImageRef.current);
        dragImageRef.current = null;
      }
    };
  }, []);

  // Disable text selection while timing modal is open
  useEffect(() => {
    if (showConfirmModal) {
      document.body.classList.add('timing-no-select');
    } else {
      document.body.classList.remove('timing-no-select');
    }
    return () => {
      document.body.classList.remove('timing-no-select');
    };
  }, [showConfirmModal]);

  // Close word popup on click outside
  useEffect(() => {
    if (!selectedWordForPopup) return;

    const handleClickOutside = (e) => {
      if (!e.target.closest('.timing-word-popup') && !e.target.closest('.timing-reassign-word')) {
        setSelectedWordForPopup(null);
      }
    };

    const handleContextMenuOutside = (e) => {
      if (!e.target.closest('.timing-word-popup') && !e.target.closest('.timing-reassign-word')) {
        setSelectedWordForPopup(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('contextmenu', handleContextMenuOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleContextMenuOutside);
    };
  }, [selectedWordForPopup]);

  // Keep drag ghost synced with cursor and clean up on end
  useEffect(() => {
    if (!draggedWordId) return;

    const handleWindowDragOver = (event) => {
      event.preventDefault();
      updateGhostPosition(event.clientX, event.clientY);
    };

    const handleWindowDrop = () => {
      cleanupDragGhost();
      setDraggedWordId(null);
      setDragOverSegmentId(null);
    };

    const handleWindowDragEnd = () => {
      cleanupDragGhost();
      setDraggedWordId(null);
      setDragOverSegmentId(null);
    };

    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('drop', handleWindowDrop);
    window.addEventListener('dragend', handleWindowDragEnd);

    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('drop', handleWindowDrop);
      window.removeEventListener('dragend', handleWindowDragEnd);
    };
  }, [draggedWordId, updateGhostPosition, cleanupDragGhost]);

  return (
    <>
      {/* Stats Bar */}
      <div className="prose-stats">
        <div className="stats-left">
          <div className="stat-item">
            <span className="stat-value">{stats.wordCount}</span>
            <span className="stat-label">{t('words')}</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-value">{stats.wpm}</span>
            <span className="stat-label">{t('wpm')}</span>
          </div>
        </div>
        <div className="stats-right">
          <button 
            className={`focus-toggle ${focusModeEnabled ? 'active' : ''} tooltip-trigger`}
            onClick={() => setFocusModeEnabled(!focusModeEnabled)}
            data-tooltip={t('Toggle Focus Mode')}
            aria-label={t('Toggle Focus Mode')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="6" />
              <circle cx="12" cy="12" r="2" fill="currentColor" />
            </svg>
            <span>{t('Focus')}</span>
          </button>
        </div>
      </div>

      <div 
        ref={containerRef}
        className={`prose-container ${(isArabizi || isNonArabic) ? 'ltr-mode' : ''} ${focusModeEnabled ? 'focus-mode' : ''}`}
        style={{ 
          fontFamily: isNonArabic
            ? (themeSettings.latinFontFamily || 'Inter, system-ui, sans-serif') 
            : (themeSettings.proseFontFamily || 'Noto Naskh Arabic'),
          fontSize: `${themeSettings.proseFontSize || 1.25}rem`
        }}
        onClick={() => {
          setSelectedWord(null);
          setPopupAnchor(null);
        }}
      >
        {allWords.length === 0 ? (
          <div className="text-center text-slate-400 py-12">{t('No text to display')}</div>
        ) : (
          <p className="prose-text">
            {(() => {
              const displayItems = [];
              for (let i = 0; i < allWords.length; i++) {
                const { word } = allWords[i];
                if (word.type === 'punctuation' && word.attaches_to === 'previous') {
                  continue;
                }
                const nextItem = allWords[i + 1];
                const nextWord = nextItem?.word;
                const attachedPunct = (showPunctuation && nextWord?.type === 'punctuation' && nextWord?.attaches_to === 'previous')
                  ? nextWord : null;
                displayItems.push({ word, attachedPunct });
              }

              const getWordText = (item) => `${formatDisplayText(getDisplayText(item.word))}${item.attachedPunct ? formatDisplayText(getDisplayText(item.attachedPunct)) : ''}`;
              const isLatinItem = (item) => {
                const phrase = getPhraseForWord(item.word.id);
                if (phrase?.activeAlternativeId === 'alt_code_switched') return true;
                const text = getWordText(item);
                const hasLatin = /[A-Za-z]/.test(text);
                const hasArabic = /[\u0600-\u06FF]/.test(text);
                return hasLatin && !hasArabic;
              };

              const renderWord = (item) => {
                const { word, attachedPunct } = item;
                const isActive = word.id === activeWordId;
                const isProtected = !!word.is_protected;
                const phrase = getPhraseForWord(word.id);
                const hasAlternatives = phrase?.alternatives?.length > 0;
                const showPhraseUi = !isNonArabic;
                const focusClass = getFocusClass(word.id);

                const isSearchMatch = searchMatchIds.has(word.id);
                const isCurrentSearchMatch = word.id === currentSearchMatchId;
                const isTimingSelected = timingSelectionWordIds.includes(word.id);

                const speakerColorStyle = (themeSettings.speakerColorEnabled && speakerMeta.wordSpeakerColorMap[word.id] && !isActive)
                  ? { color: speakerMeta.wordSpeakerColorMap[word.id] }
                  : {};

                const isAutoSpeakerChange = themeSettings.speakerDashEnabled && speakerMeta.speakerChangeIds.has(word.id);
                const showSpeakerDash = isAutoSpeakerChange && word.speakerDash !== false;

                const classes = [
                  'prose-word',
                  isActive && 'active-word',
                  isProtected && 'protected-word',
                  phrase && showPhraseUi && 'phrase-word',
                  hasAlternatives && showPhraseUi && 'has-alternatives',
                  selectedWord?.id === word.id && 'selected',
                  contextMenu?.wordId === word.id && 'context-menu-active',
                  isSearchMatch && 'search-match',
                  isCurrentSearchMatch && 'search-match-current',
                  isTimingSelected && 'timing-selected',
                  changedWordIds.has(word.id) && 'word-just-changed',
                  newWordIds.has(word.id) && 'word-new',
                  focusClass
                ].filter(Boolean).join(' ');

                return (
                  <span
                    ref={(el) => {
                      wordRefsMap.current[word.id] = el;
                      if (isActive) activeWordRef.current = el;
                    }}
                    className={classes}
                    style={speakerColorStyle}
                    onClick={isProtected ? undefined : (e) => handleWordClick(e, word)}
                    onContextMenu={isProtected ? (e) => e.preventDefault() : (e) => handleWordContextMenu(e, word)}
                    onDoubleClick={isProtected ? undefined : (e) => handleWordDoubleClick(e, word)}
                  >
                    {showSpeakerDash && <span className="speaker-dash" style={{ opacity: 0.5 }}>– </span>}
                    {formatDisplayText(getDisplayText(word))}{attachedPunct ? formatDisplayText(getDisplayText(attachedPunct)) : ''}
                  </span>
                );
              };

              // For non-Arabic languages (Arabizi, English, French), skip run-splitting
              // since everything is already LTR and doesn't need special bidirectional handling
              if (isNonArabic) {
                return displayItems.map((item, idx) => (
                  <React.Fragment key={item.word.id}>
                    {renderWord(item)}
                    {idx < displayItems.length - 1 && ' '}
                  </React.Fragment>
                ));
              }

              // For mixed Arabic/Latin content, split into runs with proper direction handling
              const runs = [];
              for (let i = 0; i < displayItems.length; i++) {
                const item = displayItems[i];
                const latin = isLatinItem(item);
                const lastRun = runs[runs.length - 1];
                if (!lastRun || lastRun.isLatin !== latin) {
                  runs.push({ isLatin: latin, items: [item] });
                } else {
                  lastRun.items.push(item);
                }
              }

              return runs.map((run, runIdx) => (
                <React.Fragment key={`${runIdx}-${run.items[0]?.word.id}`}>
                  {run.isLatin ? (
                    <span 
                      dir="ltr" 
                      style={{ 
                        unicodeBidi: 'isolate', 
                        display: 'inline-block',
                        fontFamily: themeSettings.latinFontFamily || 'Inter'
                      }}
                    >
                      {run.items.map((item, idx) => (
                        <React.Fragment key={item.word.id}>
                          {renderWord(item)}
                          {idx < run.items.length - 1 && ' '}
                        </React.Fragment>
                      ))}
                    </span>
                  ) : (
                    run.items.map((item, idx) => (
                      <React.Fragment key={item.word.id}>
                        {renderWord(item)}
                        {idx < run.items.length - 1 && ' '}
                      </React.Fragment>
                    ))
                  )}
                  {runIdx < runs.length - 1 && ' '}
                </React.Fragment>
              ));
            })()}
          </p>
        )}
      </div>

      {/* Word Editor Popup - Normal click */}
      {selectedWord && popupAnchor && (
        <InlineWordEditor
          word={selectedWord}
          anchorRect={popupAnchor}
          isNonArabic={isNonArabic}
          onSave={handleSaveWord}
          onClose={() => {
            setSelectedWord(null);
            setPopupAnchor(null);
          }}
          onOpenTiming={() => {
            const wordId = selectedWord.id;
            setSelectedWord(null);
            setPopupAnchor(null);
            if (onSwitchToStudio) {
              onSwitchToStudio(wordId);
            }
          }}
          phrase={getPhraseForWord(selectedWord.id)}
          onSelectAlternative={selectPhraseAlternative}
          displayMode={displayMode}
          fontFamily={themeSettings.proseFontFamily}
        />
      )}

      {/* Word Context Menu (Right-click) */}
      {contextMenu && contextMenu.skeletonIndex !== -1 && createPortal(
        <div 
          className="word-context-menu"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 10000
          }}
        >
          {/* Add/Remove actions - only for original view, not translation view */}
          {!isTranslationView && (
            <>
              <button
                className="context-menu-item"
                onClick={() => {
                  const skeleton = displayData.skeleton[contextMenu.skeletonIndex];
                  if (!skeleton) return;
                  
                  const wordIndex = skeleton.words.indexOf(contextMenu.wordId);
                  const currentWord = wordsMap[contextMenu.wordId];
                  
                  // Generate new word
                  const newWordId = `w${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                  const newWord = {
                    id: newWordId,
                    text: 'جديد',
                    type: 'word',
                    start_time: currentWord?.start_time ? currentWord.start_time - 0.25 : 0,
                    end_time: currentWord?.start_time || 0.5,
                    highlightColor: null,
                    break: false
                  };
                  
                  // Insert before current word
                  const newWordIds = [
                    ...skeleton.words.slice(0, wordIndex),
                    newWordId,
                    ...skeleton.words.slice(wordIndex)
                  ];
                  
                  const updatedWords = [...captionData.words, newWord].sort((a, b) =>
                    (a.start_time || 0) - (b.start_time || 0)
                  );
                  
                  const updatedWordsMap = {};
                  for (const w of updatedWords) { updatedWordsMap[w.id] = w; }
                  const firstWord = updatedWordsMap[newWordIds[0]];
                  const lastWord = updatedWordsMap[newWordIds[newWordIds.length - 1]];
                  
                  const newSkeleton = captionData.skeleton.map((skel, idx) =>
                    idx === contextMenu.skeletonIndex ? {
                      ...skel,
                      words: newWordIds,
                      start_time: firstWord?.start_time ?? skel.start_time,
                      end_time: lastWord?.end_time ?? skel.end_time
                    } : skel
                  );
                  
                  updateCaptionData({ skeleton: newSkeleton, words: updatedWords });
                  markWordsAsChanged(newWordId);
                  markWordsAsNew(newWordId);
                  setContextMenu(null);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>{t('Add word before')}</span>
              </button>
              <button
                className="context-menu-item"
                onClick={() => {
                  addWord(contextMenu.skeletonIndex, contextMenu.wordId, 'جديد');
                  setContextMenu(null);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>{t('Add word after')}</span>
              </button>
              <div className="context-menu-divider"></div>
              <button
                className={`context-menu-item danger ${
                  displayData.skeleton[contextMenu.skeletonIndex]?.words.length <= 1 ? 'disabled' : ''
                }`}
                onClick={() => {
                  const skeleton = displayData.skeleton[contextMenu.skeletonIndex];
                  if (skeleton && skeleton.words.length > 1) {
                    removeWord(contextMenu.skeletonIndex, contextMenu.wordId);
                  }
                  setContextMenu(null);
                }}
                disabled={displayData.skeleton[contextMenu.skeletonIndex]?.words.length <= 1}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
                <span>{t('Remove word')}</span>
              </button>
            </>
          )}
          {isTranslationView && (
            <div className="context-menu-item disabled" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
              <span className="text-xs text-slate-400">{t('Editing translation structure not available')}</span>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Timing Selection Guide - Show throughout selection process */}
      {timingSelectionWordIds.length >= 1 && timingSelectionStartWordId && (
        <div className="timing-selection-bar guide">
          <div className="timing-selection-icon pulse">
            <ClockIcon />
          </div>
          <div className="timing-selection-info">
            <span className="timing-selection-count">
              🎯 {t('Sync Out-of-Sync Words')} {timingSelectionWordIds.length >= 2 && t('({count} selected)', { count: timingSelectionWordIds.length })}
            </span>
            <span className="timing-selection-hint">
              {rich(t('<b>Shift+click</b> to select all words that are out of sync in a sequence'), { b: (c) => <strong>{c}</strong> })}
            </span>
          </div>
          {timingSelectionWordIds.length >= 2 && (
            <button 
              className="timing-selection-btn primary"
              onClick={() => setShowConfirmModal(true)}
            >
              <ClockIcon />
              {t('Adjust Timing')}
            </button>
          )}
          {timingSelectionWordIds.length >= 2 && (
          <button 
              className="timing-selection-btn"
              onClick={() => setShowMergeWordsModal(true)}
            >
              {t('Merge Words')}
            </button>
          )}
          <button 
            className="timing-selection-btn cancel tooltip-trigger"
            onClick={() => {
              setShowConfirmModal(false);
              setShowMergeWordsModal(false);
              setTimingSelectionWordIds([]);
              setTimingSelectionStartWordId(null);
            }}
            data-tooltip={t('Cancel selection')}
            aria-label={t('Cancel selection')}
          >
            <XIcon />
          </button>
        </div>
      )}

      {/* Merge Words Modal */}
      {showMergeWordsModal && timingSelectionWordIds.length >= 2 && (
        <div className="timing-confirm-overlay" onClick={() => setShowMergeWordsModal(false)}>
          <div className="timing-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="timing-confirm-header">
              <span className="timing-confirm-icon">🧩</span>
              <h3>{t('Merge Selected Words')}</h3>
            </div>
            <p className="timing-confirm-text">
              {t('This will combine the selected words into a single word inside the same chunk.')}
            </p>
            <div className={`timing-confirm-sequence ${(isArabizi || isTranslationView) ? 'ltr' : 'rtl'}`}>
              {timingSelectionWordIds.map((wordId) => {
                const word = wordsMap[wordId];
                return (
                  <span key={wordId} className="timing-confirm-word">
                    {getDisplayText(word)}
                  </span>
                );
              })}
            </div>
            <div className="timing-confirm-actions">
              <button className="timing-confirm-btn cancel" onClick={() => setShowMergeWordsModal(false)}>
                {t('Cancel')}
              </button>
              <button 
                className="timing-confirm-btn confirm"
                onClick={() => {
                  const orderedIds = [...timingSelectionWordIds].sort((a, b) => {
                    const wa = captionWordsMap[a];
                    const wb = captionWordsMap[b];
                    return (wa?.start_time ?? 0) - (wb?.start_time ?? 0);
                  });
                  const result = mergeMultipleWords(0, orderedIds);
                  if (result?.success) {
                    showToast?.('Words merged.', 'success');
                    setShowMergeWordsModal(false);
                    setTimingSelectionWordIds([]);
                    setTimingSelectionStartWordId(null);
                  } else {
                    showToast?.(result?.error || 'Merge failed.', 'warning');
                  }
                }}
              >
                {t('Merge Words')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timing Adjustment Modal */}
      {showConfirmModal && timingSelectionWordIds.length >= 2 && (() => {
        // Get selected words data for the modal
        const modalSelectedWords = timingSelectionWordIds
                    .map(id => captionWordsMap[id])
                    .filter(Boolean);
                  
        // Initialize audio segments when modal opens (if not already done)
        if (audioSegments.length === 0 && modalSelectedWords.length > 0) {
          const segments = modalSelectedWords.map((word, index) => ({
            id: `seg_${index}`,
            startTime: word.start_time,
            endTime: word.end_time,
            originalWordId: word.id
          }));
          setAudioSegments(segments);

          const assignments = {};
          modalSelectedWords.forEach((word, index) => {
            assignments[word.id] = `seg_${index}`;
          });
          setWordAssignments(assignments);
        }

        const formatTime = (time) => {
          const mins = Math.floor(time / 60);
          const secs = (time % 60).toFixed(2);
          return mins > 0 ? `${mins}:${secs.padStart(5, '0')}` : `${secs}s`;
        };

        const getWordsForSegment = (segmentId) => {
          return modalSelectedWords.filter(w => wordAssignments[w.id] === segmentId);
        };

        const playAudioSegment = (segment) => {
          const video = videoRef?.current;
          if (!video) return;
          clearTimingSegmentListener();
          timingSegmentEndRef.current = segment.endTime;
          video.currentTime = segment.startTime;
          setPlayingSegmentId(segment.id);
          const handleTimeUpdate = () => {
            if (timingSegmentEndRef.current !== null && video.currentTime >= timingSegmentEndRef.current) {
              video.pause();
              setPlayingSegmentId(null);
              clearTimingSegmentListener();
            }
          };
          timingSegmentListenerRef.current = handleTimeUpdate;
          video.addEventListener('timeupdate', handleTimeUpdate);
          video.play();
        };

        const handleWordPopupClick = (e, wordId) => {
          e.stopPropagation();
          const rect = e.target.getBoundingClientRect();
          setPopupPosition({
            x: rect.left + rect.width / 2,
            y: rect.top
          });
          setSelectedWordForPopup(wordId);
        };

        const closeWordPopup = () => {
          setSelectedWordForPopup(null);
        };

        // Drag and drop handlers
        const handleDragStart = (e, wordId) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', wordId);

          const rect = e.target.getBoundingClientRect();
          const styles = window.getComputedStyle(e.target);
          const backgroundColor =
            styles.backgroundColor && styles.backgroundColor !== 'rgba(0, 0, 0, 0)'
              ? styles.backgroundColor
              : '#f8fafc';

          // Store offset from click position to element top-left
          dragOffsetRef.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
          };

          // Create custom ghost that follows cursor
          const ghost = document.createElement('div');
          ghost.textContent = e.target.textContent;
          ghost.style.cssText = `
            position: fixed;
            left: ${rect.left}px;
            top: ${rect.top}px;
            background-color: ${backgroundColor};
            color: ${styles.color || '#0f172a'};
            padding: ${styles.padding};
            border-radius: ${styles.borderRadius};
            font-size: ${styles.fontSize};
            font-weight: ${styles.fontWeight};
            font-family: ${styles.fontFamily};
            border: ${styles.borderWidth || '1px'} ${styles.borderStyle || 'solid'} ${styles.borderColor || '#e2e8f0'};
            line-height: ${styles.lineHeight};
            z-index: 99999;
            pointer-events: none;
            white-space: nowrap;
          `;
          document.body.appendChild(ghost);
          dragImageRef.current = ghost;

          // Hide native drag image with transparent 1x1 pixel
          const emptyImg = new Image();
          emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
          e.dataTransfer.setDragImage(emptyImg, 0, 0);

          setDraggedWordId(wordId);
        };

        const handleDrag = (e) => {
          updateGhostPosition(e.clientX, e.clientY);
        };

        const handleDragEnd = () => {
          cleanupDragGhost();
          setDraggedWordId(null);
          setDragOverSegmentId(null);
        };

        const handleDragOver = (e, segmentId) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (dragOverSegmentId !== segmentId) {
            setDragOverSegmentId(segmentId);
          }
        };

        const handleDragLeave = (e) => {
          // Only clear if we're leaving the segment entirely
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX;
          const y = e.clientY;

          if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setDragOverSegmentId(null);
          }
        };

        const handleDrop = (e, segmentId) => {
          e.preventDefault();
          e.stopPropagation();

          const wordId = e.dataTransfer.getData('text/plain') || draggedWordId;
          if (wordId && segmentId && wordAssignments[wordId] !== segmentId) {
            setWordAssignments(prev => ({ ...prev, [wordId]: segmentId }));
          }

          cleanupDragGhost();
          setDraggedWordId(null);
          setDragOverSegmentId(null);
        };

        const moveWordUp = (wordId) => {
          const currentSegmentId = wordAssignments[wordId];
          const currentSegmentIndex = audioSegments.findIndex(s => s.id === currentSegmentId);
          if (currentSegmentIndex <= 0) return;

          const previousSegmentId = audioSegments[currentSegmentIndex - 1].id;
          setWordAssignments(prev => ({ ...prev, [wordId]: previousSegmentId }));
          closeWordPopup();
        };

        const moveWordDown = (wordId) => {
          const currentSegmentId = wordAssignments[wordId];
          const currentSegmentIndex = audioSegments.findIndex(s => s.id === currentSegmentId);
          if (currentSegmentIndex >= audioSegments.length - 1) return;

          const nextSegmentId = audioSegments[currentSegmentIndex + 1].id;
          setWordAssignments(prev => ({ ...prev, [wordId]: nextSegmentId }));
          closeWordPopup();
        };

        const mergeSegments = (segmentIndex) => {
          if (segmentIndex >= audioSegments.length - 1) return;

          const currentSegment = audioSegments[segmentIndex];
          const nextSegment = audioSegments[segmentIndex + 1];

          const mergedSegment = {
            id: currentSegment.id,
            startTime: currentSegment.startTime,
            endTime: nextSegment.endTime,
            originalWordId: currentSegment.originalWordId
          };

          const newSegments = [
            ...audioSegments.slice(0, segmentIndex),
            mergedSegment,
            ...audioSegments.slice(segmentIndex + 2)
          ];

          // Update assignments - move words from next segment to current segment
          const newAssignments = { ...wordAssignments };
          Object.keys(newAssignments).forEach(wordId => {
            if (newAssignments[wordId] === nextSegment.id) {
              newAssignments[wordId] = currentSegment.id;
            }
          });

          // Get all words that will be in the merged segment
          const wordsInMergedSegment = modalSelectedWords.filter(w => 
            newAssignments[w.id] === currentSegment.id
          );

          // If there are multiple words, apply proportional timing immediately
          if (wordsInMergedSegment.length > 1) {
            const totalChars = wordsInMergedSegment.reduce((sum, w) => sum + (getDisplayText(w)?.length || 1), 0);
            const segmentDuration = mergedSegment.endTime - mergedSegment.startTime;

            // Calculate proportional timings for each word in the merged segment
            let currentStart = mergedSegment.startTime;
            const mergedWordIdSet = new Set(wordsInMergedSegment.map(w => w.id));
            const mergedWordIndexMap = {};
            wordsInMergedSegment.forEach((w, idx) => { mergedWordIndexMap[w.id] = idx; });

            const updatedWords = captionData.words.map(word => {
              if (!mergedWordIdSet.has(word.id)) return word;

              const wordIndex = mergedWordIndexMap[word.id];
              const charRatio = (getDisplayText(word)?.length || 1) / totalChars;
              const wordDuration = segmentDuration * charRatio;

              const newWord = {
                ...word,
                start_time: currentStart,
                end_time: wordIndex === wordsInMergedSegment.length - 1 ? mergedSegment.endTime : currentStart + wordDuration
              };
              currentStart += wordDuration;
              return newWord;
            });

            // Update skeleton timings
            const updatedWordsLookup = {};
            for (const w of updatedWords) { updatedWordsLookup[w.id] = w; }
            const mergedWordIds = mergedWordIdSet;
            const updatedSkeletons = captionData.skeleton.map(skel => {
              const firstWordId = skel.words[0];
              const lastWordId = skel.words[skel.words.length - 1];
              let newSkel = { ...skel };

              const firstWord = updatedWordsLookup[firstWordId];
              const lastWord = updatedWordsLookup[lastWordId];

              if (firstWord && mergedWordIds.has(firstWordId)) newSkel.start_time = firstWord.start_time;
              if (lastWord && mergedWordIds.has(lastWordId)) newSkel.end_time = lastWord.end_time;

              return newSkel;
            });
                    
            // Apply caption data update
            updateCaptionData({ ...captionData, words: updatedWords, skeleton: updatedSkeletons });
            markWordsAsChanged(wordsInMergedSegment.map(w => w.id));
          }

          setAudioSegments(newSegments);
          setWordAssignments(newAssignments);
        };

        const handleProportionalApply = () => {
          if (modalSelectedWords.length >= 2) {
            const startTime = Math.min(...modalSelectedWords.map(w => w.start_time ?? 0));
            const endTime = Math.max(...modalSelectedWords.map(w => w.end_time ?? 0));
            const totalDuration = endTime - startTime;
            const totalChars = modalSelectedWords.reduce((sum, w) => sum + (w.text?.length || 1), 0);
            const sortedSelectedWords = [...modalSelectedWords].sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0));

                    const newTimings = {};
            let currTime = startTime;
                    sortedSelectedWords.forEach((word, index) => {
                      const charRatio = (word.text?.length || 1) / totalChars;
                      const wordDuration = totalDuration * charRatio;
                      newTimings[word.id] = {
                start_time: currTime,
                end_time: index === sortedSelectedWords.length - 1 ? endTime : currTime + wordDuration
                      };
              currTime += wordDuration;
                    });
                    
            const updatedWords = captionData.words.map(w => newTimings[w.id] ? { ...w, ...newTimings[w.id] } : w);
            const updatedWordIds = new Set(Object.keys(newTimings));

            const updatedSkeletons = captionData.skeleton.map(skel => {
              const firstWordId = skel.words[0];
              const lastWordId = skel.words[skel.words.length - 1];
              let newSkel = { ...skel };
              if (updatedWordIds.has(firstWordId)) newSkel.start_time = newTimings[firstWordId].start_time;
              if (updatedWordIds.has(lastWordId)) newSkel.end_time = newTimings[lastWordId].end_time;
              return newSkel;
                    });
                    
            updateCaptionData({ ...captionData, words: updatedWords, skeleton: updatedSkeletons });
                    markWordsAsChanged(Object.keys(newTimings));
                  }
                  
          handleCloseModal();
        };

        const handleReassignApply = () => {
          const updatedWords = captionData.words.map(word => {
            if (!timingSelectionWordIds.includes(word.id)) return word;

            const assignedSegmentId = wordAssignments[word.id];
            const segment = audioSegments.find(s => s.id === assignedSegmentId);
            if (!segment) return word;

            const wordsInSegment = modalSelectedWords.filter(w => wordAssignments[w.id] === assignedSegmentId);
            const wordIndex = wordsInSegment.findIndex(w => w.id === word.id);

            if (wordsInSegment.length === 1) {
              return { ...word, start_time: segment.startTime, end_time: segment.endTime };
            } else {
              const totalChars = wordsInSegment.reduce((sum, w) => sum + (getDisplayText(w)?.length || 1), 0);
              const segmentDuration = segment.endTime - segment.startTime;

              let currentStart = segment.startTime;
              for (let i = 0; i < wordIndex; i++) {
                const w = wordsInSegment[i];
                const charRatio = (getDisplayText(w)?.length || 1) / totalChars;
                currentStart += segmentDuration * charRatio;
              }

              const charRatio = (getDisplayText(word)?.length || 1) / totalChars;
              const wordDuration = segmentDuration * charRatio;

              return {
                ...word,
                start_time: currentStart,
                end_time: wordIndex === wordsInSegment.length - 1 ? segment.endTime : currentStart + wordDuration
              };
            }
          });

          const updatedWordIds = new Set(timingSelectionWordIds);
          const updatedWordsLookup = {};
          for (const w of updatedWords) { updatedWordsLookup[w.id] = w; }
          const updatedSkeletons = captionData.skeleton.map(skel => {
            const firstWordId = skel.words[0];
            const lastWordId = skel.words[skel.words.length - 1];
            let newSkel = { ...skel };

            const firstWord = updatedWordsLookup[firstWordId];
            const lastWord = updatedWordsLookup[lastWordId];

            if (firstWord && updatedWordIds.has(firstWordId)) newSkel.start_time = firstWord.start_time;
            if (lastWord && updatedWordIds.has(lastWordId)) newSkel.end_time = lastWord.end_time;

            return newSkel;
          });

          updateCaptionData({ ...captionData, words: updatedWords, skeleton: updatedSkeletons });
          markWordsAsChanged(timingSelectionWordIds);
          handleCloseModal();
        };

        const handleCloseModal = () => {
                  setShowConfirmModal(false);
                  setTimingSelectionWordIds([]);
                  setTimingSelectionStartWordId(null);
          setAudioSegments([]);
          setWordAssignments({});
          setTimingAdjustMode('reassign');
          setSelectedWordForPopup(null);
          clearTimingSegmentListener();
          setPlayingSegmentId(null);
          setDraggedWordId(null);
          setDragOverSegmentId(null);
        };

        const hasReassignChanges = Object.keys(wordAssignments).some((wordId, index) => {
          return wordAssignments[wordId] !== `seg_${index}`;
        }) || audioSegments.length !== modalSelectedWords.length;

        const handleModalClick = (e) => {
          e.stopPropagation();
          if (!e.target.closest('.timing-word-popup') && !e.target.closest('.timing-reassign-word')) {
            setSelectedWordForPopup(null);
          }
        };

        return (
          <div className="timing-confirm-overlay" onClick={handleCloseModal}>
            <div className="timing-confirm-modal timing-modal-large" onClick={handleModalClick}>
              <div className="timing-confirm-header">
                <div className="timing-header-left">
                  <span className="timing-confirm-icon">🎯</span>
                  <h3>{t('Adjust Word Timing')}</h3>
                  <span className="timing-word-count">{t('{count} words', { count: modalSelectedWords.length })}</span>
                </div>
                <button className="timing-close-btn" onClick={handleCloseModal} aria-label={t('Close')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              {/* Tab Switcher */}
              <div className="timing-tab-switcher">
                <button 
                  className={`timing-tab-btn ${timingAdjustMode === 'reassign' ? 'active' : ''}`}
                  onClick={() => setTimingAdjustMode('reassign')}
                >
                  {t('Reassign Word Timing')}
                </button>
                <button 
                  className={`timing-tab-btn ${timingAdjustMode === 'proportional' ? 'active' : ''}`}
                  onClick={() => setTimingAdjustMode('proportional')}
                >
                  {t('Proportional Adjustment')}
                </button>
              </div>

              {/* Combined text preview */}
              <div className={`timing-text-preview ${(isArabizi || isTranslationView) ? 'ltr' : 'rtl'}`}>
                <span className="preview-label">{t('Selected:')}</span>
                <span className="preview-text">
                  {modalSelectedWords.map(w => getDisplayText(w)).join(' ')}
                </span>
              </div>

              {timingAdjustMode === 'reassign' ? (
                <>
                  <div className="timing-reassign-container">
                    <div className="timing-reassign-header">
                      <div className="timing-col-header">{t('Audio Segments')}</div>
                      <div className="timing-col-header">{t('Assigned Words')}</div>
                    </div>
                    
                    <div className="timing-reassign-content">
                      {audioSegments.map((segment, index) => {
                        const wordsInSegment = getWordsForSegment(segment.id);
                        const isCurrentlyPlaying = playingSegmentId === segment.id;

                        return (
                          <React.Fragment key={segment.id}>
                            <div className="timing-reassign-row">
                              {/* Audio */}
                              <div className="timing-audio-segment">
                                <button 
                                  className={`timing-audio-play-btn ${isCurrentlyPlaying ? 'playing' : ''}`}
                                  onClick={() => playAudioSegment(segment)}
                                >
                                  {isCurrentlyPlaying ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                      <rect x="6" y="4" width="4" height="16" />
                                      <rect x="14" y="4" width="4" height="16" />
                                    </svg>
                                  ) : (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  )}
                                </button>
                                <div className="timing-audio-timing" dir="ltr">
                                  <span className="timing-value">{formatTime(segment.startTime)}</span>
                                  <span className="timing-separator">→</span>
                                  <span className="timing-value">{formatTime(segment.endTime)}</span>
                                </div>
                              </div>

                              <div className="timing-connector">
                                <div className="timing-connector-line"></div>
                              </div>

                              {/* Words - Drop Target */}
                              <div 
                                className={`timing-words-segment ${wordsInSegment.length === 0 ? 'empty' : ''} ${dragOverSegmentId === segment.id ? 'drag-over' : ''} ${draggedWordId ? 'drop-target' : ''}`}
                                onDragOver={(e) => handleDragOver(e, segment.id)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, segment.id)}
                              >
                                {wordsInSegment.length === 0 ? (
                                  <span className="timing-empty-label">
                                    {draggedWordId ? (dragOverSegmentId === segment.id ? `✓ ${t('Drop here')}` : t('Drop here')) : t('No word')}
                                  </span>
                                ) : (
                                  wordsInSegment.map(word => (
                                    <span 
                                      key={word.id}
                                      className={`timing-reassign-word ${draggedWordId === word.id ? 'dragging' : ''}`}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, word.id)}
                                      onDrag={handleDrag}
                                      onDragEnd={handleDragEnd}
                                      onClick={(e) => !draggedWordId && handleWordPopupClick(e, word.id)}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (!draggedWordId) handleWordPopupClick(e, word.id);
                                      }}
                                    >
                                      {getDisplayText(word)}
                                    </span>
                                  ))
                                )}
                              </div>
                            </div>

                            {/* Merge Button */}
                            {index < audioSegments.length - 1 && (
                              <div className="timing-merge-row">
                                <button 
                                  className="timing-merge-btn tooltip-trigger"
                                  onClick={() => mergeSegments(index)}
                                  data-tooltip={t('Merge with next segment')}
                                  aria-label={t('Merge with next segment')}
                                >
                                  {t('Merge the audios into one')}
              </button>
            </div>
                            )}
                          </React.Fragment>
                        );
                      })}
          </div>
        </div>

                  <div className="timing-help-text">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    {t('Drag words to reassign, or click for up/down options.')}
                  </div>

                  <div className="timing-confirm-actions">
                    <button className="timing-confirm-btn cancel" onClick={handleCloseModal}>
                      {t('Cancel')}
                    </button>
                    <button 
                      className="timing-confirm-btn confirm" 
                      onClick={handleReassignApply}
                      disabled={!hasReassignChanges}
                    >
                      {t('Apply Mapping')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="timing-proportional-info">
                    <p className="timing-confirm-text">
                      {rich(t('This will redistribute timing <b>proportionally</b> based on word length.'), { b: (c) => <strong>{c}</strong> })}
                    </p>
                    <div className={`timing-confirm-sequence ${(isArabizi || isTranslationView) ? 'ltr' : 'rtl'}`}>
                      {timingSelectionWordIds.map((wordId) => {
                        const word = wordsMap[wordId];
                        return (
                          <span key={wordId} className="timing-confirm-word">
                            {getDisplayText(word)}
                          </span>
                        );
                      })}
                    </div>
                    <p className="timing-confirm-warning">
                      ⚡ {rich(t('<b>Tip:</b> Use this when words have correct order but timing is uneven.'), { b: (c) => <strong>{c}</strong> })}
                    </p>
                  </div>
                  <div className="timing-confirm-actions">
                    <button className="timing-confirm-btn cancel" onClick={handleCloseModal}>
                      {t('Cancel')}
                    </button>
                    <button className="timing-confirm-btn confirm" onClick={handleProportionalApply}>
                      {t('Adjust Proportionally')}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Word Movement Popup */}
            {selectedWordForPopup && (
              <div 
                className="timing-word-popup"
                style={{ left: popupPosition.x, top: popupPosition.y }}
                onClick={e => e.stopPropagation()}
              >
                {(() => {
                  const currentSegmentId = wordAssignments[selectedWordForPopup];
                  const currentSegmentIndex = audioSegments.findIndex(s => s.id === currentSegmentId);
                  const canMoveUp = currentSegmentIndex > 0;
                  const canMoveDown = currentSegmentIndex < audioSegments.length - 1;

                  return (
                    <>
                      <button 
                        className={`timing-popup-btn ${!canMoveUp ? 'disabled' : ''}`}
                        onClick={() => canMoveUp && moveWordUp(selectedWordForPopup)}
                        disabled={!canMoveUp}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="18 15 12 9 6 15" />
                        </svg>
                        {t('Move Up')}
                      </button>
                      <button 
                        className={`timing-popup-btn ${!canMoveDown ? 'disabled' : ''}`}
                        onClick={() => canMoveDown && moveWordDown(selectedWordForPopup)}
                        disabled={!canMoveDown}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                        {t('Move Down')}
                      </button>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        );
      })()}
    </>
  );
};

export default TextProseRenderer;
